const PROFIT_COLORS = {
  invoices: "#64dd01",
  orders: "#007FFF",
  articulo: "#ef6c00",
  /** Grupo sin coste: profit = factura Directo (SL.INV). */
  directoInv: "#00897b",
  noCosts: "#9c27b0",
};

export function getPedidoCostSource(row) {
  const raw = String(row?.CostSource || "")
    .trim()
    .toLowerCase();
  if (raw === "pedido" || raw === "articulo" || raw === "none") return raw;
  if (Number(row?.PedidoCompraE || 0) !== 0) return "pedido";
  return "none";
}

/**
 * Margen:
 * - Grupo (2) sin coste → profit = factura Directo (SL.INV).
 * - Con coste (pedido/ficha) o tipocont 1 → profit = venta − |coste|
 *   (o factura venta − factura compra si ambas existen).
 */
export function getPedidoProfitMeta(row) {
  const costSource = getPedidoCostSource(row);
  const tipocont = getPedidoTipocont(row);
  const venta = Math.abs(Number(row.VentaBaseEUR || 0));
  const compra = Math.abs(Number(row.PedidoCompraE || 0));
  const invCost = Math.abs(Number(row.InvCostMoneda || 0));
  const ownInv = Math.abs(Number(row.InvSalesMoneda || 0));
  const pairInv = Math.abs(Number(row._pairInvSalesMoneda || 0));
  const hasPurchase = compra > 0 || invCost > 0;

  let profitAbs = 0;
  let profitPct = 0;
  let type = "none";

  // Tipo 2 sin coste: el margen Stage es la factura del Directo.
  if (tipocont === "2" && !hasPurchase) {
    const directoInv = ownInv > 0 ? ownInv : pairInv;
    if (directoInv > 0) {
      type = "directoInv";
      profitAbs = directoInv;
      profitPct = venta > 0 ? (directoInv / venta) * 100 : 0;
    } else {
      type = "noCosts";
    }
  } else if (ownInv > 0 && invCost > 0) {
    // Factura venta + factura compra (tipocont 1 típico / SFERA).
    type = "invoices";
    profitAbs = ownInv - invCost;
    profitPct = ownInv > 0 ? ((ownInv - invCost) / ownInv) * 100 : 0;
  } else if (hasPurchase && venta > 0) {
    type = costSource === "articulo" ? "articulo" : "orders";
    profitAbs = venta - compra;
    profitPct = ((venta - compra) / venta) * 100;
  } else if (!hasPurchase) {
    type = "noCosts";
  }

  return {
    profitAbs,
    profitPct,
    type,
    color: PROFIT_COLORS[type] || undefined,
    hasInvoices: type === "invoices" || type === "directoInv",
    hasPurchase,
    costSource,
  };
}

export function formatPedidoAmount(
  val,
  { decimals = 2, emptyZero = false } = {},
) {
  if (val === undefined || val === null) return "";
  if (emptyZero && Number(val) === 0) return "";
  return Number(val).toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatProfitPct(profitPct) {
  return `${Number(profitPct).toLocaleString("es-ES", { maximumFractionDigits: 2 })} %`;
}

export const EMPRESA_OPTIONS = [
  { value: "stage", label: "Stage" },
  { value: "erna", label: "ERNA" },
  { value: "fusion", label: "Fusión" },
];

export const PROFIT_LEGEND = [
  { key: "orders", label: "Pedido", color: PROFIT_COLORS.orders },
  { key: "invoices", label: "Factura", color: PROFIT_COLORS.invoices },
  {
    key: "directoInv",
    label: "Factura Directo (= profit)",
    color: PROFIT_COLORS.directoInv,
  },
  { key: "articulo", label: "Coste ficha", color: PROFIT_COLORS.articulo },
  { key: "noCosts", label: "Sin compra", color: PROFIT_COLORS.noCosts },
];

const DOC_KIND_COLORS = {
  invoices: PROFIT_COLORS.invoices,
  orders: PROFIT_COLORS.orders,
};

/** TIPOCONT 2 = Grupo (venta comercial); 1 = Directo / parte Stage. */
export const TIPOCONT_META = {
  2: {
    tipocont: "2",
    key: "grupo",
    label: "Grupo",
    short: "G2",
    color: "#2e7d32",
  },
  1: {
    tipocont: "1",
    key: "directo",
    label: "Directo",
    short: "D1",
    color: "#1565c0",
  },
};

export const TIPOCONT_LEGEND = [
  {
    key: "grupo",
    label: "Grupo (venta comercial)",
    color: TIPOCONT_META["2"].color,
  },
  { key: "directo", label: "Directo", color: TIPOCONT_META["1"].color },
  {
    key: "parte",
    label: "Directo · parte (mismo PO Grupo)",
    color: "#7b1fa2",
  },
];

export function getPedidoTipocont(row) {
  const raw = String(row?.TIPOCONT ?? "").trim();
  return raw || "1";
}

export function getPedidoTipocontMeta(row) {
  const tipocont = getPedidoTipocont(row);
  const base = TIPOCONT_META[tipocont] || TIPOCONT_META["1"];
  if (row?._tipocontRole === "parte") {
    return {
      ...base,
      key: "parte",
      label: "Directo · parte",
      short: "D1·",
      color:
        TIPOCONT_LEGEND.find((item) => item.key === "parte")?.color ||
        base.color,
      isParte: true,
      isPrimary: false,
    };
  }
  return {
    ...base,
    isParte: false,
    isPrimary: tipocont === "2" || row?._tipocontRole !== "parte",
  };
}

/**
 * Marca cada línea: si el mismo cliente+PO tiene TIPOCONT=2, las de TIPOCONT=1
 * son "parte" (desglose, no sumar en totales comerciales).
 * En Grupo, copia factura/servicio de la parte Directo para el badge Pedido/Factura.
 */
export function annotatePedidosTipocontRoles(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const grupoDocs = new Set();
  const pairByDoc = new Map();

  for (const row of list) {
    if (getPedidoTipocont(row) === "2") {
      grupoDocs.add(`${getPedidoCliente(row)}|${getPedidoDoc(row)}`);
    }
  }

  for (const row of list) {
    const docKey = `${getPedidoCliente(row)}|${getPedidoDoc(row)}`;
    if (getPedidoTipocont(row) !== "1" || !grupoDocs.has(docKey)) continue;
    const prev = pairByDoc.get(docKey) || {
      inv: 0,
      venta: 0,
      serv: 0,
      ptes: 0,
      un: 0,
      anul: 0,
    };
    prev.inv += Number(row.InvSalesMoneda || 0);
    prev.venta += Number(row.VentaBaseEUR || 0);
    prev.serv += Number(row.UN_Serv || 0);
    prev.ptes += Number(row.UN_Ptes || 0);
    prev.un += Number(row.VentasUN || 0);
    prev.anul += Number(row.UN_Anul || 0);
    pairByDoc.set(docKey, prev);
  }

  return list.map((row) => {
    const tipocont = getPedidoTipocont(row);
    const docKey = `${getPedidoCliente(row)}|${getPedidoDoc(row)}`;
    const hasGrupoPair = grupoDocs.has(docKey);
    const role = tipocont === "1" && hasGrupoPair ? "parte" : "primary";
    const pair = pairByDoc.get(docKey);
    return {
      ...row,
      _tipocont: tipocont,
      _tipocontRole: role,
      _hasGrupoPair: hasGrupoPair,
      _pairInvSalesMoneda: pair?.inv || 0,
      _pairVentaBaseEUR: pair?.venta || 0,
      _pairUN_Serv: pair?.serv || 0,
      _pairUN_Ptes: pair?.ptes || 0,
      _pairUN_Anul: pair?.anul || 0,
      _pairVentasUN: pair?.un || 0,
    };
  });
}

export function isPedidoPrimaryForTotals(row) {
  return row?._tipocontRole !== "parte";
}

/** Moneda origen A3; los importes de venta ya van en €. */
export function formatPedidoCurrency(row) {
  const mon = String(row?.CODMON || "")
    .trim()
    .toUpperCase();
  if (!mon) return "";
  if (mon === "EUR" || mon === "EURO") return "EUR";
  return `${mon}→€`;
}

export function getPedidoCliente(row) {
  return String(row?.CLIPRO || "").trim();
}

export function getPedidoDoc(row) {
  return String(row?.PorchDoc || "").trim();
}

export function getPedidoEstado(row) {
  // Grupo con pareja Directo: el estado operativo vive en la parte Directo.
  const usePair =
    getPedidoTipocont(row) === "2" &&
    row?._hasGrupoPair &&
    (Number(row?._pairVentasUN || 0) > 0 ||
      Number(row?._pairUN_Serv || 0) > 0 ||
      Number(row?._pairUN_Ptes || 0) > 0);
  const ptes = usePair
    ? Number(row._pairUN_Ptes || 0)
    : Number(row?.UN_Ptes || 0);
  const serv = usePair
    ? Number(row._pairUN_Serv || 0)
    : Number(row?.UN_Serv || 0);
  if (ptes > 0) return "abierto";
  if (serv > 0) return "servido";
  return "cerrado";
}

/**
 * Factura = hay factura en la línea, o (en Grupo) en la parte Directo del mismo PO.
 * Así EASYWEAR Grupo no queda como "Pedido" cuando Directo ya facturó.
 */
export function getPedidoDocKind(row) {
  const own = Number(row?.InvSalesMoneda || 0) > 0;
  const pair = Number(row?._pairInvSalesMoneda || 0) > 0;
  return own || pair ? "invoices" : "orders";
}

export function getPedidoDocKindMeta(row) {
  const kind = getPedidoDocKind(row);
  const fromLegend = PROFIT_LEGEND.find((item) => item.key === kind);
  return {
    kind,
    label: fromLegend?.label || "Pedido",
    color: DOC_KIND_COLORS[kind] || PROFIT_COLORS.orders,
  };
}

export function listPedidoClientes(rows = []) {
  return [
    ...new Set(
      (Array.isArray(rows) ? rows : [])
        .map((row) => getPedidoCliente(row))
        .filter(Boolean),
    ),
  ].sort((a, b) => a.localeCompare(b, "es"));
}

export function matchesPedidoVentaSearch(row, search) {
  const q = String(search || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  return [
    row?.CLIPRO,
    row?.REPALIAS,
    row?.ARTALIAS,
    row?.PorchDoc,
    row?.CAR1,
    row?.CODART,
  ].some((value) =>
    String(value || "")
      .toLowerCase()
      .includes(q),
  );
}

export function filterPedidosVenta(
  rows = [],
  {
    search = "",
    estado = "todos",
    cliente = "",
    docKind = "todos",
    showParteDirecto = false,
  } = {},
) {
  const clienteKey = String(cliente || "").trim();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!showParteDirecto && row?._tipocontRole === "parte") return false;
    if (!matchesPedidoVentaSearch(row, search)) return false;
    if (estado === "abiertos" && getPedidoEstado(row) !== "abierto")
      return false;
    if (estado === "servidos" && getPedidoEstado(row) !== "servido")
      return false;
    if (clienteKey && getPedidoCliente(row) !== clienteKey) return false;
    if (docKind !== "todos" && getPedidoDocKind(row) !== docKind) return false;
    return true;
  });
}

export const PEDIDO_VENTA_ORDEN_DEFAULT = "entrega-asc";

export const PEDIDO_VENTA_ORDEN_OPTIONS = [
  { value: "entrega-asc", label: "Entrega: cercanas primero" },
  { value: "entrega-desc", label: "Entrega: lejanas primero" },
  { value: "fecha-desc", label: "Fecha PO: nuevas primero" },
  { value: "fecha-asc", label: "Fecha PO: antiguas primero" },
  { value: "cliente", label: "Cliente A → Z" },
];

function pedidoDateMs(row, field) {
  const raw = row?.[field];
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function tipocontSortKey(row) {
  const tipocont = getPedidoTipocont(row);
  if (tipocont === "2") return 0;
  if (row?._tipocontRole === "parte") return 2;
  return 1;
}

function cmpWithTipocont(primaryCmp) {
  return (a, b) => {
    const main = primaryCmp(a, b);
    if (main !== 0) return main;
    const tipocontDiff = tipocontSortKey(a) - tipocontSortKey(b);
    if (tipocontDiff !== 0) return tipocontDiff;
    return String(a?.CODART || "").localeCompare(String(b?.CODART || ""), "es");
  };
}

export function sortPedidosVenta(
  rows = [],
  orden = PEDIDO_VENTA_ORDEN_DEFAULT,
) {
  const list = [...(Array.isArray(rows) ? rows : [])];
  const cmpDate = (field, dir) => (a, b) => {
    const av = pedidoDateMs(a, field);
    const bv = pedidoDateMs(b, field);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return (av - bv) * dir;
  };

  if (orden === "entrega-desc") {
    return list.sort(cmpWithTipocont(cmpDate("DeliveryDate", -1)));
  }
  if (orden === "fecha-asc") {
    return list.sort(cmpWithTipocont(cmpDate("FECHA", 1)));
  }
  if (orden === "fecha-desc") {
    return list.sort(cmpWithTipocont(cmpDate("FECHA", -1)));
  }
  if (orden === "cliente") {
    return list.sort(
      cmpWithTipocont((a, b) =>
        getPedidoCliente(a).localeCompare(getPedidoCliente(b), "es"),
      ),
    );
  }
  return list.sort(cmpWithTipocont(cmpDate("DeliveryDate", 1)));
}

export function createPedidoFocus(type, row) {
  const cliente = getPedidoCliente(row);
  const doc = getPedidoDoc(row);
  if (type === "cliente") {
    if (!cliente) return null;
    return { type: "cliente", cliente };
  }
  if (!cliente && !doc) return null;
  return { type: "doc", cliente, doc };
}

export function isSamePedidoFocus(a, b) {
  if (!a || !b || a.type !== b.type) return false;
  if (a.type === "cliente") return a.cliente === b.cliente;
  return a.cliente === b.cliente && a.doc === b.doc;
}

export function togglePedidoFocus(current, next) {
  if (!next) return null;
  return isSamePedidoFocus(current, next) ? null : next;
}

export function rowMatchesPedidoFocus(row, focus) {
  if (!focus) return true;
  if (focus.type === "cliente") return getPedidoCliente(row) === focus.cliente;
  return (
    getPedidoCliente(row) === focus.cliente && getPedidoDoc(row) === focus.doc
  );
}

export function describePedidoFocus(focus, count) {
  if (!focus) return "";
  if (focus.type === "cliente") {
    return `${focus.cliente} · ${count} ${count === 1 ? "línea" : "líneas"}`;
  }
  const doc = focus.doc || "sin PO";
  return `${focus.cliente || "Cliente"} · ${doc} · ${count} ${
    count === 1 ? "línea" : "líneas"
  }`;
}

/**
 * Unidades operativas a pintar: en Grupo con pareja Directo, las del Directo
 * (el Grupo suele llevar todo anulado y 0 servido).
 */
export function getPedidoDisplayUnits(row) {
  const usePair =
    getPedidoTipocont(row) === "2" &&
    row?._hasGrupoPair &&
    Number(row?._pairVentasUN || 0) > 0;
  if (usePair) {
    return {
      un: Number(row._pairVentasUN || 0),
      serv: Number(row._pairUN_Serv || 0),
      ptes: Number(row._pairUN_Ptes || 0),
      anul: Number(row._pairUN_Anul || 0),
      fromPair: true,
    };
  }
  return {
    un: Number(row?.VentasUN || 0),
    serv: Number(row?.UN_Serv || 0),
    ptes: Number(row?.UN_Ptes || 0),
    anul: Number(row?.UN_Anul || 0),
    fromPair: false,
  };
}

/**
 * SL.INV.€ a pintar:
 * - Tipocont 1: solo factura propia.
 * - Tipocont 2 sin coste: factura Directo (es el profit).
 * - Tipocont 2 con coste: solo factura propia del Grupo (no mezclar Directo).
 */
export function getPedidoDisplayInvSales(row) {
  const own = Number(row?.InvSalesMoneda || 0);
  if (own > 0) return own;
  if (getPedidoTipocont(row) !== "2" || !row?._hasGrupoPair) return own;
  const hasPurchase =
    Number(row?.PedidoCompraE || 0) !== 0 ||
    Number(row?.InvCostMoneda || 0) !== 0;
  if (hasPurchase) return own;
  return Number(row._pairInvSalesMoneda || 0);
}

export function getPedidoPendingAmount(row) {
  const units = getPedidoDisplayUnits(row);
  const unit = Number(row?.UNPedVentaE || 0);
  if (units.ptes > 0 && Number.isFinite(unit) && unit > 0) {
    return units.ptes * unit;
  }
  const sale = Math.abs(Number(row?.VentaBaseEUR || 0));
  // Grupo+Directo: el gap de factura vive en la parte Directo (no en el Grupo a 0).
  if (
    getPedidoTipocont(row) === "2" &&
    row?._hasGrupoPair &&
    Number(row?._pairVentaBaseEUR || 0) > 0
  ) {
    const pairSale = Math.abs(Number(row._pairVentaBaseEUR || 0));
    const pairInv = Math.abs(Number(row._pairInvSalesMoneda || 0));
    const pairGap = Math.max(0, pairSale - pairInv);
    if (pairSale <= 0) return 0;
    return sale * (pairGap / pairSale);
  }
  const invoiced = Math.abs(getPedidoDisplayInvSales(row));
  const gap = sale - invoiced;
  return gap > 0 ? gap : 0;
}

const INITIAL_SKIP =
  /^(S\.?A\.?|S\.?L\.?|B\.?V\.?|LTD|LIMITED|INC|GMBH|EUROPE|INTERNATIONAL|TRADING|THE|AND|DE|DEL|LA|EL|&)$/i;

export function getClienteInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/[\s,/]+/)
    .map((part) => part.replace(/[()[\]{}]/g, ""))
    .filter((part) => part && !INITIAL_SKIP.test(part));
  const use = parts.length
    ? parts
    : String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
  if (!use.length) return "?";
  if (use.length === 1) return use[0].slice(0, 2).toUpperCase();
  return `${use[0][0]}${use[1][0]}`.toUpperCase();
}

const AVATAR_PALETTE = [
  "#1976d2",
  "#00897b",
  "#6d4c41",
  "#7b1fa2",
  "#1565c0",
  "#2e7d32",
  "#c62828",
  "#ef6c00",
  "#455a64",
  "#5c6bc0",
];

export function getClienteAvatarColor(name) {
  const text = String(name || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

export function summarizePedidoDoc(rows = []) {
  const annotated = annotatePedidosTipocontRoles(rows);
  const primary = annotated.filter((row) => isPedidoPrimaryForTotals(row));
  const partes = annotated.filter((row) => !isPedidoPrimaryForTotals(row));
  const tipoconts = [
    ...new Set(annotated.map((row) => getPedidoTipocont(row))),
  ];
  const hasGrupoAndParte = tipoconts.includes("2") && partes.length > 0;

  let venta = 0;
  let ventaParte = 0;
  for (const row of primary) venta += Number(row?.VentaBaseEUR || 0);
  for (const row of partes) ventaParte += Number(row?.VentaBaseEUR || 0);

  // Operativa (servido / factura / pendiente): Directo si hay pareja; si no, primary.
  const opsRows = hasGrupoAndParte ? partes : primary;
  let facturado = 0;
  let pendiente = 0;
  let ptes = 0;
  let serv = 0;
  let un = 0;
  for (const row of opsRows) {
    facturado += Number(row?.InvSalesMoneda || 0);
    pendiente += getPedidoPendingAmount(row);
    ptes += Number(row?.UN_Ptes || 0);
    serv += Number(row?.UN_Serv || 0);
    un += Number(row?.VentasUN || 0);
  }

  // Pendiente € comercial: escala el hueco Directo al importe Grupo.
  if (hasGrupoAndParte && ventaParte > 0) {
    const pairGap = Math.max(0, ventaParte - facturado);
    pendiente = venta * (pairGap / ventaParte);
  }

  const estado = ptes > 0 ? "abierto" : serv > 0 ? "servido" : "cerrado";
  return {
    venta,
    ventaParte,
    facturado,
    pendiente,
    ptes,
    serv,
    un,
    estado,
    count: annotated.length,
    hasGrupoAndParte,
    tipoconts,
    opsFromDirecto: hasGrupoAndParte,
  };
}

export function summarizePedidos(rows = []) {
  let totalSales = 0;
  let totalProfit = 0;
  let pendingUnits = 0;
  let pendingAmount = 0;
  let parteCount = 0;

  const list = Array.isArray(rows) ? rows : [];
  list.forEach((row) => {
    if (!isPedidoPrimaryForTotals(row)) {
      parteCount += 1;
      return;
    }
    totalSales += Number(row.VentaBaseEUR || row.InvSalesMoneda || 0);
    totalProfit += getPedidoProfitMeta(row).profitAbs;
    pendingUnits += getPedidoDisplayUnits(row).ptes;
    pendingAmount += getPedidoPendingAmount(row);
  });

  const marginPct =
    Math.abs(totalSales) > 0 ? (totalProfit / Math.abs(totalSales)) * 100 : 0;

  return {
    totalSales,
    totalProfit,
    marginPct,
    pendingUnits,
    pendingAmount,
    count: list.length,
    primaryCount: list.length - parteCount,
    parteCount,
  };
}

export function getYearOptions(count = 6) {
  const current = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => current - i);
}
